/**
 * Cross-workbench edit router (Phase B4) — the WRITE half of the scene
 * protocol. The ledger (A2) is the read half: every workbench sees every
 * object through `useDocumentStore.sceneObjects`. This module completes the
 * loop: an edit STARTED in any workbench on ANY source's object is routed
 * back to the owning workbench's rich store, so the object's living copy
 * stays single and re-mirrors through the ledger to every other viewport.
 *
 * Routing contract (mirrors docs/architecture/single-app-integration-plan.md
 * Golden Rule 1 — stores own their rich model, the router never writes
 * parallel copies):
 * - `cad`    → useAppStore entities (undo: app history)
 * - `bim`    → useBIMStore kernel mutations (undo: kernel command stack)
 * - `sketch` → useSketchStore solids (undo: sketch HistoryAction)
 *
 * Consumers: GumballLayer (rigid transforms), ERASE/delete flows,
 * PropertiesPanel (protocol-level fields) — anything that must operate on a
 * mixed-source selection without caring where each object was drawn.
 */

import * as THREE from 'three';
import { useAppStore, type Entity } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { useSketchStore } from '../../state/useSketchStore';
import { useDocumentStore } from '../../state/useDocumentStore';
import type { SceneClipboardEntry } from '../../state/commandAuxStores';
import type { SceneSource } from './types';
import { transformDataBag, dataBagPoints, refreshKernelTessellation } from '../utils/dataTransform';
import { transformSolid, transformSolidFaces, transformSolidEdges, transformSolidSubobjects, transformSolidCorners } from '../sketch/transform';
import { parseFaceSelectionId, parseEdgeSelectionId, parseVertexSelectionId, edgeSelectionId } from '../sketch/types';
import { solidSharedEdge, edgeChainKeys, buildTopology, solidVertexPoints } from '../sketch/topology';
import type { SketchSolid } from '../sketch/types';
import type { BIMElement } from '../types';

type Pt3 = [number, number, number];

/** Selection partitioned by owning workbench. */
export interface SceneSelectionPartition {
    cad: string[];
    bim: string[];
    sketch: string[];
}

const EMPTY_PARTITION: SceneSelectionPartition = { cad: [], bim: [], sketch: [] };

/**
 * Resolve a composite `solidId:faceId` selection id to a LIVE sketch face.
 * Returns null for anything else — CAD entity ids (`cad:...`) also contain
 * a colon, so the solid+face pair must actually exist in the sketch store.
 */
export function sketchFaceSelectionOf(id: string): { solidId: string; faceId: string } | null {
    const parsed = parseFaceSelectionId(id);
    if (!parsed) return null;
    const solid = useSketchStore.getState().solids[parsed.solidId] as SketchSolid | undefined;
    return solid?.faces.some((f) => f.id === parsed.faceId) ? parsed : null;
}

/**
 * Resolve a composite `solidId:edge:<key>` selection id to a LIVE sketch
 * shared edge. Returns null for anything else — the key must resolve in the
 * solid's CURRENT topology (stale keys die with the selection).
 */
export function sketchEdgeSelectionOf(id: string): { solidId: string; edgeKey: string } | null {
    const parsed = parseEdgeSelectionId(id);
    if (!parsed) return null;
    const solid = useSketchStore.getState().solids[parsed.solidId] as SketchSolid | undefined;
    if (!solid) return null;
    return solidSharedEdge(solid, parsed.edgeKey) ? parsed : null;
}

/**
 * Resolve a composite `solidId:vertex:<key>` selection id to a LIVE sketch
 * corner. Returns null for anything else — the weld key must still hash a
 * contour point of the solid's CURRENT loops (stale keys die with the
 * selection).
 */
export function sketchVertexSelectionOf(id: string): { solidId: string; vertexKey: string } | null {
    const parsed = parseVertexSelectionId(id);
    if (!parsed) return null;
    const solid = useSketchStore.getState().solids[parsed.solidId] as SketchSolid | undefined;
    if (!solid) return null;
    return solidVertexPoints(solid, parsed.vertexKey).length > 0 ? parsed : null;
}

/** Resolve the owning source of an id: ledger first, owning stores as the
 *  fallback for ids that predate their ledger mirror. */
export function sceneSourceOf(id: string): SceneSource | null {
    const fromLedger = useDocumentStore.getState().sceneObjects[id]?.source;
    if (fromLedger) return fromLedger;
    if (useAppStore.getState().entities[id]) return 'cad';
    const sketch = useSketchStore.getState();
    if (sketch.solids[id] || sketch.edges[id]) return 'sketch';
    if (useBIMStore.getState().project.elements.has(id)) return 'bim';
    return null;
}

/** Split a selection into per-owner id lists (unknown ids are dropped). */
export function partitionSceneSelection(ids: string[]): SceneSelectionPartition {
    const out: SceneSelectionPartition = { ...EMPTY_PARTITION, cad: [], bim: [], sketch: [] };
    ids.forEach((id) => {
        const source = sceneSourceOf(id);
        if (source === 'cad' || source === 'nurbs') out.cad.push(id);
        else if (source === 'bim') out.bim.push(id);
        else if (source === 'sketch') out.sketch.push(id);
    });
    return out;
}

// ------------------------------------------------------------------ anchors

/** Representative world points of one scene object (gizmo anchor, bounds). */
export function sceneObjectAnchors(id: string): Pt3[] {
    // A face selection anchors on the face's own contour, not its solid's
    // bounds — the gumball sits on the picked face.
    const face = sketchFaceSelectionOf(id);
    if (face) {
        const solid = useSketchStore.getState().solids[face.solidId];
        const f = solid?.faces.find((entry) => entry.id === face.faceId);
        if (!f) return [];
        return [...f.outer, ...f.holes.flat()] as Pt3[];
    }
    // An edge selection anchors on its chain's endpoints — the gumball sits
    // centered on the curve being dragged.
    const edge = sketchEdgeSelectionOf(id);
    if (edge) {
        const solid = useSketchStore.getState().solids[edge.solidId];
        if (!solid) return [];
        const chain = new Set(edgeChainKeys(solid, edge.edgeKey));
        const pts: Pt3[] = [];
        buildTopology(solid).edges.forEach((e) => {
            if (!chain.has(e.id)) return;
            pts.push([e.a.x, e.a.y, e.a.z], [e.b.x, e.b.y, e.b.z]);
        });
        return pts;
    }
    // A vertex selection anchors on the corner itself (the welded contour
    // points are one world point — dedupe to it).
    const vertex = sketchVertexSelectionOf(id);
    if (vertex) {
        const solid = useSketchStore.getState().solids[vertex.solidId];
        if (!solid) return [];
        const pts = solidVertexPoints(solid, vertex.vertexKey);
        return pts.length > 0 ? [[pts[0][0], pts[0][1], pts[0][2]]] : [];
    }
    const source = sceneSourceOf(id);
    if (source === 'sketch') {
        const sketch = useSketchStore.getState();
        // Annotations anchor on their own geometry: the dimension on its
        // measured points + dim line, the label on anchor + leader.
        const dimension = sketch.dimensions[id];
        if (dimension) return [dimension.a as Pt3, dimension.b as Pt3, dimension.offset as Pt3];
        const label = sketch.labels[id];
        if (label) return [label.anchor as Pt3, label.leader as Pt3];
        const edge = sketch.edges[id];
        if (edge) return [edge.a as Pt3, edge.b as Pt3];
        const solid = sketch.solids[id];
        if (!solid) return [];
        // Analytic loops are small: face origins + contour vertices give a
        // faithful centroid without paying for triangulation.
        const pts: Pt3[] = [];
        solid.faces.forEach((face) => {
            pts.push(face.origin as Pt3);
            face.outer.forEach((p) => pts.push(p as Pt3));
        });
        return pts;
    }
    if (source === 'bim') {
        const element = useBIMStore.getState().project.elements.get(id);
        if (!element) return [];
        const gd = (element.geometryData ?? {}) as Record<string, unknown>;
        const pts: Pt3[] = [];
        (['position', 'start', 'end', 'center', 'location'] as const).forEach((key) => {
            const v = gd[key];
            if (Array.isArray(v) && v.length >= 2 && typeof v[0] === 'number') {
                pts.push([v[0] as number, v[1] as number, (v[2] as number) ?? 0]);
            }
        });
        return pts;
    }
    // cad (and anything stored as an app-store entity, NURBS included)
    const entity = useAppStore.getState().entities[id];
    if (!entity) return [];
    return dataBagPoints(entity.data) as Pt3[];
}

/** Centroid across a whole selection; null when nothing resolves. */
export function sceneSelectionCentroid(ids: string[]): THREE.Vector3 | null {
    let count = 0;
    const centroid = new THREE.Vector3();
    ids.forEach((id) => {
        sceneObjectAnchors(id).forEach((p) => {
            centroid.x += p[0];
            centroid.y += p[1];
            centroid.z += p[2] ?? 0;
            count++;
        });
    });
    if (count === 0) return null;
    centroid.divideScalar(count);
    return centroid;
}

/** Minimal-rotation frame taking +X onto `dir` — the remaining axes land
 *  as close to world as possible, so the gizmo never spins arbitrarily. */
function frameFromX(dir: THREE.Vector3): THREE.Quaternion {
    if (dir.lengthSq() < 1e-12) return new THREE.Quaternion();
    return new THREE.Quaternion().setFromUnitVectors(X_AXIS, dir.normalize());
}

/** Minimal-rotation frame taking +Z onto `normal`. */
function frameFromZ(normal: THREE.Vector3): THREE.Quaternion {
    if (normal.lengthSq() < 1e-12) return new THREE.Quaternion();
    return new THREE.Quaternion().setFromUnitVectors(Z_AXIS, normal.normalize());
}

/** Direction of the two farthest-apart points — a selection chain's true
 *  run, independent of point ordering. */
function farthestPairDirection(pts: Pt3[]): THREE.Vector3 | null {
    let best: THREE.Vector3 | null = null;
    let bestLen = 0;
    for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
            const len = Math.hypot(
                pts[j][0] - pts[i][0], pts[j][1] - pts[i][1], pts[j][2] - pts[i][2],
            );
            if (len > bestLen) {
                bestLen = len;
                best = new THREE.Vector3(
                    pts[j][0] - pts[i][0], pts[j][1] - pts[i][1], pts[j][2] - pts[i][2],
                );
            }
        }
    }
    return bestLen > 1e-9 && best ? best : null;
}

/** Normalized end−start when both points exist and differ. */
function runDirection(start: unknown, end: unknown): THREE.Vector3 | null {
    if (!isArrayPoint(start) || !isArrayPoint(end)) return null;
    const dir = new THREE.Vector3(
        end[0] - start[0], end[1] - start[1], (end[2] ?? 0) - (start[2] ?? 0),
    );
    return dir.lengthSq() > 1e-12 ? dir.normalize() : null;
}

function isArrayPoint(v: unknown): v is number[] {
    return Array.isArray(v) && v.length >= 2 && typeof v[0] === 'number';
}

/**
 * Orientation of ONE scene object's gumball frame: X rides the object's
 * dominant run (edge chain, wall/line start→end, a solid's longest contour
 * edge) or Z rides its dominant plane normal (face selections); world-
 * aligned when no frame can be derived.
 */
export function sceneObjectFrame(id: string): THREE.Quaternion {
    // A face composite rides the face's own plane — the blue arrow pulls
    // the face along its normal (same convention as the section plane).
    const face = sketchFaceSelectionOf(id);
    if (face) {
        const solid = useSketchStore.getState().solids[face.solidId];
        const f = solid?.faces.find((entry) => entry.id === face.faceId);
        if (f) return frameFromZ(new THREE.Vector3(...f.normal));
        return new THREE.Quaternion();
    }
    // An edge composite rides its chain's overall run — the red arrow
    // slides the edge along itself.
    if (sketchEdgeSelectionOf(id)) {
        const dir = farthestPairDirection(sceneObjectAnchors(id));
        return dir ? frameFromX(dir) : new THREE.Quaternion();
    }
    const sketch = useSketchStore.getState();
    const dimension = sketch.dimensions[id];
    if (dimension) {
        // A dimension's gumball rides its measured run (a→b).
        return frameFromX(new THREE.Vector3(
            dimension.b[0] - dimension.a[0],
            dimension.b[1] - dimension.a[1],
            (dimension.b[2] ?? 0) - (dimension.a[2] ?? 0),
        ));
    }
    if (sketch.labels[id]) return new THREE.Quaternion();
    const looseEdge = sketch.edges[id];
    if (looseEdge) {
        const a = looseEdge.a as Pt3;
        const b = looseEdge.b as Pt3;
        return frameFromX(new THREE.Vector3(b[0] - a[0], b[1] - a[1], b[2] - a[2]));
    }
    const solid = sketch.solids[id];
    if (solid) {
        // Whole solid: the longest contour edge is its "run" — the length
        // of a wall-like mass, the span of a slab. Deterministic tie-break:
        // the first longest edge in face order wins.
        let best: THREE.Vector3 | null = null;
        let bestLen = 0;
        solid.faces.forEach((f) => {
            for (let i = 0; i < f.outer.length; i++) {
                const a = f.outer[i];
                const b = f.outer[(i + 1) % f.outer.length];
                const len = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
                if (len > bestLen) {
                    bestLen = len;
                    best = new THREE.Vector3(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
                }
            }
        });
        return best ? frameFromX(best) : new THREE.Quaternion();
    }
    // BIM element: the run of its base curve (wall/beam/duct/pipe
    // geometryData all carry start→end).
    const element = useBIMStore.getState().project.elements.get(id) as BIMElement | undefined;
    if (element) {
        const gd = (element.geometryData ?? {}) as Record<string, unknown>;
        const dir = runDirection(gd.start, gd.end);
        return dir ? frameFromX(dir) : new THREE.Quaternion();
    }
    // CAD entity: line-like data bags carry start→end too.
    const entity = useAppStore.getState().entities[id];
    if (entity) {
        const data = (entity.data ?? {}) as Record<string, unknown>;
        const dir = runDirection(data.start, data.end);
        return dir ? frameFromX(dir) : new THREE.Quaternion();
    }
    return new THREE.Quaternion();
}

/**
 * World pose the gumball assumes for a selection: centered on the
 * selection's bounding-box midpoint — the visual center, which the anchor
 * point average (sceneSelectionCentroid) misses whenever point sampling is
 * uneven — and, for a single-object selection, oriented to that object's
 * own frame (sceneObjectFrame). Multi-selections stay world-aligned.
 */
export function sceneSelectionAnchor(ids: string[]): {
    position: THREE.Vector3;
    quaternion: THREE.Quaternion;
} | null {
    const box = new THREE.Box3();
    let found = false;
    ids.forEach((id) => {
        sceneObjectAnchors(id).forEach((p) => {
            box.expandByPoint(new THREE.Vector3(p[0], p[1], p[2] ?? 0));
            found = true;
        });
    });
    if (!found) return null;
    return {
        position: box.getCenter(new THREE.Vector3()),
        quaternion: ids.length === 1 ? sceneObjectFrame(ids[0]) : new THREE.Quaternion(),
    };
}

const X_AXIS = new THREE.Vector3(1, 0, 0);
const Z_AXIS = new THREE.Vector3(0, 0, 1);

// ------------------------------------------------------------------ delete

/** Delete scene objects across every source as one undoable step per
 *  owner. Returns how many objects were actually removed. */
export function deleteSceneObjects(ids: string[], label = 'Erase'): number {
    const { cad, bim, sketch } = partitionSceneSelection(ids);
    let deleted = 0;

    if (cad.length > 0) {
        const app = useAppStore.getState();
        const snapshots = cad.map((id) => app.entities[id]).filter(Boolean).map((e) => ({ ...e })) as Entity[];
        cad.forEach((id) => app.deleteEntity(id));
        const taken = snapshots.length;
        if (taken > 0) {
            app.pushHistory({
                type: 'cmd.delete',
                timestamp: Date.now(),
                data: { count: taken, label },
                undo: () => { const a = useAppStore.getState(); snapshots.forEach((e) => a.addEntity(e)); },
                redo: () => { const a = useAppStore.getState(); snapshots.forEach((e) => a.deleteEntity(e.id)); },
            });
        }
        deleted += taken;
    }

    if (sketch.length > 0) {
        // Sketch owns four citizen shapes: analytic solids, loose edges and
        // the dimension/label annotations.
        const sketchStore = useSketchStore.getState();
        const solidIds = sketch.filter((id) => sketchStore.solids[id]);
        const edgeIds = sketch.filter((id) => sketchStore.edges[id]);
        const dimensionIds = sketch.filter((id) => sketchStore.dimensions[id]);
        const labelIds = sketch.filter((id) => sketchStore.labels[id]);
        if (solidIds.length > 0) {
            sketchStore.removeSolids(solidIds, label);
            deleted += solidIds.length;
        }
        if (edgeIds.length > 0) {
            sketchStore.removeLooseEdges(edgeIds, label);
            deleted += edgeIds.length;
        }
        if (dimensionIds.length > 0) {
            sketchStore.removeDimensions(dimensionIds, label);
            deleted += dimensionIds.length;
        }
        if (labelIds.length > 0) {
            sketchStore.removeLabels(labelIds, label);
            deleted += labelIds.length;
        }
    }

    if (bim.length > 0) {
        const bimStore = useBIMStore.getState();
        bim.forEach((id) => {
            if (bimStore.project.elements.has(id)) {
                bimStore.removeElement(id);
                deleted++;
            }
        });
    }

    return deleted;
}

// ---------------------------------------------------------------- transform

/** Point keys of BIM geometryData that carry world-space positions. */
const BIM_POINT_KEYS = ['position', 'start', 'end', 'center', 'location'] as const;

/** Return a copy of BIM geometryData with every point key mapped through
 *  `matrix` (undefined when nothing moved — callers can skip the write). */
function bimGeometryThrough(
    gd: Record<string, unknown> | undefined,
    matrix: THREE.Matrix4
): Record<string, unknown> | null {
    if (!gd) return null;
    const next = { ...gd };
    let touched = false;
    BIM_POINT_KEYS.forEach((key) => {
        const v = next[key];
        if (!Array.isArray(v) || typeof v[0] !== 'number') return;
        const p = new THREE.Vector3(v[0] as number, v[1] as number, (v[2] as number) ?? 0).applyMatrix4(matrix);
        next[key] = [p.x, p.y, p.z];
        touched = true;
    });
    return touched ? next : null;
}

/** Rigid transform of scene objects across every source as one undoable
 *  step per owner. Returns how many objects were transformed. Hosted BIM
 *  citizens (doors/windows) transform too: outside the BIM workbench they
 *  behave as block instances, and inside it the kernel re-solves them. */
export function transformSceneObjects(ids: string[], matrix: THREE.Matrix4, label = 'Transform'): number {
    // Subobject faces/edges/vertices first: composite `solidId:faceId`,
    // `solidId:edge:<key>` and `solidId:vertex:<key>` ids transform INSIDE
    // their solid (shared corners stretch the connected geometry), and a
    // parent solid id in the same selection never rides along — the
    // subobject move owns that solid for this gesture.
    const faceRefs: { solidId: string; faceId: string }[] = [];
    const edgeRefs: { solidId: string; edgeKey: string }[] = [];
    const vertexRefs: { solidId: string; vertexKey: string }[] = [];
    const subobjectParents = new Set<string>();
    const wholeIds: string[] = [];
    ids.forEach((id) => {
        const face = sketchFaceSelectionOf(id);
        if (face) {
            faceRefs.push(face);
            subobjectParents.add(face.solidId);
            return;
        }
        const edge = sketchEdgeSelectionOf(id);
        if (edge) {
            edgeRefs.push(edge);
            subobjectParents.add(edge.solidId);
            return;
        }
        const vertex = sketchVertexSelectionOf(id);
        if (vertex) {
            vertexRefs.push(vertex);
            subobjectParents.add(vertex.solidId);
            return;
        }
        wholeIds.push(id);
    });

    let transformed = 0;

    if (faceRefs.length > 0) {
        const sketchStore = useSketchStore.getState();
        const bySolid = new Map<string, string[]>();
        faceRefs.forEach(({ solidId, faceId }) => {
            const list = bySolid.get(solidId) ?? [];
            if (!list.includes(faceId)) list.push(faceId);
            bySolid.set(solidId, list);
        });
        bySolid.forEach((faceIds, solidId) => {
            const solid = sketchStore.solids[solidId];
            if (!solid) return;
            sketchStore.updateSolid(solidId, transformSolidFaces(solid, faceIds, matrix), label);
            transformed++;
        });
    }

    if (edgeRefs.length > 0) {
        const sketchStore = useSketchStore.getState();
        // Expand every composite to its full chain (Sketch's Curve entity)
        // so a curve always moves as one piece, whatever subset is selected.
        const bySolid = new Map<string, string[]>();
        edgeRefs.forEach(({ solidId, edgeKey }) => {
            const solid = sketchStore.solids[solidId];
            const list = bySolid.get(solidId) ?? [];
            const chain = solid ? edgeChainKeys(solid, edgeKey) : [edgeKey];
            chain.forEach((key) => {
                if (!list.includes(key)) list.push(key);
            });
            bySolid.set(solidId, list);
        });
        bySolid.forEach((edgeKeys, solidId) => {
            const solid = sketchStore.solids[solidId];
            if (!solid) return;
            sketchStore.updateSolid(solidId, transformSolidEdges(solid, edgeKeys, matrix), label);
            transformed++;
        });
    }

    if (vertexRefs.length > 0) {
        const sketchStore = useSketchStore.getState();
        const bySolid = new Map<string, string[]>();
        vertexRefs.forEach(({ solidId, vertexKey }) => {
            const list = bySolid.get(solidId) ?? [];
            if (!list.includes(vertexKey)) list.push(vertexKey);
            bySolid.set(solidId, list);
        });
        bySolid.forEach((vertexKeys, solidId) => {
            const solid = sketchStore.solids[solidId];
            if (!solid) return;
            const corners = vertexKeys.flatMap((key) => solidVertexPoints(solid, key));
            if (corners.length === 0) return;
            sketchStore.updateSolid(solidId, transformSolidCorners(solid, corners, matrix), label);
            transformed++;
        });
    }

    const { cad, bim, sketch } = partitionSceneSelection(
        wholeIds.filter((id) => !subobjectParents.has(id))
    );

    if (cad.length > 0) {
        const app = useAppStore.getState();
        const before = new Map<string, Record<string, any>>();
        const after = new Map<string, Record<string, any>>();
        cad.forEach((id) => {
            const e = app.entities[id];
            if (!e) return;
            const snapshot = structuredClone(e.data);
            before.set(id, snapshot);
            const next = refreshKernelTessellation(transformDataBag(structuredClone(e.data), matrix));
            after.set(id, next);
            useAppStore.getState().updateEntity(id, { data: next });
            transformed++;
        });
        if (before.size > 0) {
            app.pushHistory({
                type: 'cmd.modify',
                timestamp: Date.now(),
                data: { count: before.size, label },
                undo: () => { const a = useAppStore.getState(); before.forEach((d, id) => a.updateEntity(id, { data: d })); },
                redo: () => { const a = useAppStore.getState(); after.forEach((d, id) => a.updateEntity(id, { data: d })); },
            });
        }
    }

    if (sketch.length > 0) {
        const sketchStore = useSketchStore.getState();
        const solidIds = sketch.filter((id) => sketchStore.solids[id]);
        const edgeIds = sketch.filter((id) => sketchStore.edges[id]);
        const dimensionIds = sketch.filter((id) => sketchStore.dimensions[id]);
        const labelIds = sketch.filter((id) => sketchStore.labels[id]);
        solidIds.forEach((id) => {
            const solid = sketchStore.solids[id];
            if (!solid) return;
            sketchStore.updateSolid(id, transformSolid(solid, matrix), label);
            transformed++;
        });
        if (edgeIds.length > 0) {
            sketchStore.transformLooseEdges(edgeIds, matrix, label);
            transformed += edgeIds.length;
        }
        if (dimensionIds.length > 0) {
            sketchStore.transformDimensions(dimensionIds, matrix, label);
            transformed += dimensionIds.length;
        }
        if (labelIds.length > 0) {
            sketchStore.transformLabels(labelIds, matrix, label);
            transformed += labelIds.length;
        }
    }

    if (bim.length > 0) {
        const bimStore = useBIMStore.getState();
        bim.forEach((id) => {
            const element = bimStore.project.elements.get(id);
            if (!element) return;
            const nextGeometry = bimGeometryThrough(
                element.geometryData as Record<string, unknown> | undefined,
                matrix
            );
            if (!nextGeometry) return;
            bimStore.updateElement(id, { geometryData: nextGeometry } as Partial<BIMElement>);
            transformed++;
        });
    }

    return transformed;
}

// -------------------------------------------------------------------- copy

/**
 * Duplicate scene objects across every source (the COPY tool's engine).
 * Each owner clones its own citizens with fresh ids through `matrix`;
 * everything lands at the SAME world coordinates because all workbenches
 * share one coordinate frame (A5). Returns every created id so callers
 * can select the copies.
 */
export function copySceneObjects(ids: string[], matrix: THREE.Matrix4, label = 'Copy'): string[] {
    const { cad, bim, sketch } = partitionSceneSelection(ids);
    const newIds: string[] = [];

    if (cad.length > 0) {
        const app = useAppStore.getState();
        const clones: Entity[] = [];
        cad.forEach((id) => {
            const src = app.entities[id];
            if (!src) return;
            const nextData = refreshKernelTessellation(transformDataBag(structuredClone(src.data), matrix));
            const clone: Entity = {
                id: `cad:${crypto.randomUUID()}`,
                type: src.type,
                name: `${src.name} Copy`,
                visible: src.visible,
                locked: false,
                layer: src.layer,
                data: nextData,
            };
            clones.push(clone);
            newIds.push(clone.id);
        });
        if (clones.length > 0) {
            clones.forEach((c) => useAppStore.getState().addEntity(c));
            app.pushHistory({
                type: 'cmd.add',
                timestamp: Date.now(),
                data: { count: clones.length, label },
                undo: () => { const a = useAppStore.getState(); clones.forEach((c) => a.deleteEntity(c.id)); },
                redo: () => { const a = useAppStore.getState(); clones.forEach((c) => a.addEntity(c)); },
            });
        }
    }

    if (sketch.length > 0) {
        const sketchStore = useSketchStore.getState();
        const solidIds = sketch.filter((id) => sketchStore.solids[id]);
        const edgeIds = sketch.filter((id) => sketchStore.edges[id]);
        if (solidIds.length > 0) newIds.push(...sketchStore.copySolids(solidIds, matrix, label));
        if (edgeIds.length > 0) newIds.push(...sketchStore.copyLooseEdges(edgeIds, matrix, label));
    }

    if (bim.length > 0) {
        const bimStore = useBIMStore.getState();
        bim.forEach((id) => {
            const element = bimStore.project.elements.get(id);
            if (!element) return;
            const clone = structuredClone(element) as BIMElement;
            clone.id = crypto.randomUUID();
            clone.name = `${element.name || element.category} Copy`;
            const nextGeometry = bimGeometryThrough(
                clone.geometryData as Record<string, unknown> | undefined,
                matrix
            );
            if (nextGeometry) clone.geometryData = nextGeometry as BIMElement['geometryData'];
            // One self-undoing kernel command per clone (ADD_ELEMENT).
            bimStore.addElement(clone);
            newIds.push(clone.id);
        });
    }

    return newIds;
}

// --------------------------------------------------------------- clipboard

/**
 * Snapshot a selection into cross-workbench clipboard entries (COPYBASE's
 * engine). Every citizen is captured as its OWN source's payload, so the
 * clipboard survives workbench switches and paste can re-create each
 * citizen inside its owner.
 */
export function captureSceneClipboard(ids: string[]): SceneClipboardEntry[] {
    const entries: SceneClipboardEntry[] = [];
    ids.forEach((id) => {
        const source = sceneSourceOf(id);
        if (source === 'bim') {
            const element = useBIMStore.getState().project.elements.get(id);
            if (element) entries.push({ source: 'bim', element: structuredClone(element) });
            return;
        }
        if (source === 'sketch') {
            const sketchStore = useSketchStore.getState();
            const solid = sketchStore.solids[id];
            const edge = sketchStore.edges[id];
            if (solid) entries.push({ source: 'sketch', solid: structuredClone(solid) });
            else if (edge) entries.push({ source: 'sketchEdge', edge: structuredClone(edge) });
            return;
        }
        const entity = useAppStore.getState().entities[id];
        if (entity) entries.push({ source: 'cad', entity: structuredClone(entity) });
    });
    return entries;
}

/**
 * Paste clipboard entries back into their owners through `matrix` (PASTE's
 * engine). Everything lands at the same shared-world coordinates shifted
 * by the matrix; each owner commits with its own undo chain. Returns the
 * created ids.
 */
export function pasteSceneClipboard(entries: SceneClipboardEntry[], matrix: THREE.Matrix4, label = 'Paste'): string[] {
    const newIds: string[] = [];
    const cadClones: Entity[] = [];

    entries.forEach((entry) => {
        if (entry.source === 'cad') {
            const src = entry.entity;
            cadClones.push({
                id: `cad:${crypto.randomUUID()}`,
                type: src.type,
                name: `${src.name} (paste)`,
                visible: src.visible,
                locked: false,
                layer: src.layer,
                data: refreshKernelTessellation(transformDataBag(structuredClone(src.data), matrix)),
            });
        } else if (entry.source === 'bim') {
            const clone = structuredClone(entry.element) as BIMElement;
            clone.id = crypto.randomUUID();
            clone.name = `${clone.name || clone.category} (paste)`;
            const nextGeometry = bimGeometryThrough(
                clone.geometryData as Record<string, unknown> | undefined,
                matrix
            );
            if (nextGeometry) clone.geometryData = nextGeometry as BIMElement['geometryData'];
            // One self-undoing kernel command per paste (ADD_ELEMENT).
            useBIMStore.getState().addElement(clone);
            newIds.push(clone.id);
        } else if (entry.source === 'sketch') {
            newIds.push(...useSketchStore.getState().insertSolids([structuredClone(entry.solid)], matrix, label));
        } else {
            newIds.push(...useSketchStore.getState().insertLooseEdges([structuredClone(entry.edge)], matrix, label));
        }
    });

    if (cadClones.length > 0) {
        cadClones.forEach((c) => useAppStore.getState().addEntity(c));
        newIds.push(...cadClones.map((c) => c.id));
        useAppStore.getState().pushHistory({
            type: 'cmd.add',
            timestamp: Date.now(),
            data: { count: cadClones.length, label },
            undo: () => { const a = useAppStore.getState(); cadClones.forEach((c) => a.deleteEntity(c.id)); },
            redo: () => { const a = useAppStore.getState(); cadClones.forEach((c) => a.addEntity(c)); },
        });
    }

    return newIds;
}

// ------------------------------------------------------------------- props

/** Protocol-level fields editable from any workbench. */
export interface SceneObjectPropPatch {
    displayName?: string;
    visible?: boolean;
    locked?: boolean;
    layerId?: string;
}

/**
 * Patch protocol-level properties of one object through its owner.
 * Returns false when the id is unknown or the owner rejects the field
 * (e.g. BIM per-element visibility lives in view scope, not the element).
 */
export function patchSceneObjectProps(id: string, patch: SceneObjectPropPatch): boolean {
    const source = sceneSourceOf(id);
    if (!source) return false;

    if (source === 'sketch') {
        const sketchStore = useSketchStore.getState();
        const solid = sketchStore.solids[id];
        if (!solid) return false;
        const next: SketchSolid = {
            ...solid,
            ...(patch.displayName !== undefined ? { name: patch.displayName } : {}),
            ...(patch.visible !== undefined ? { visible: patch.visible } : {}),
            ...(patch.locked !== undefined ? { locked: patch.locked } : {}),
        };
        sketchStore.updateSolid(id, next, 'Edit Properties');
        return true;
    }

    if (source === 'bim') {
        const bimStore = useBIMStore.getState();
        const element = bimStore.project.elements.get(id);
        if (!element) return false;
        const updates: Record<string, unknown> = {};
        if (patch.displayName !== undefined) updates.name = patch.displayName;
        if (patch.locked !== undefined || patch.layerId !== undefined) {
            const parameters = { ...(element.parameters ?? {}) } as Record<string, unknown>;
            if (patch.locked !== undefined) parameters.Locked = patch.locked;
            if (patch.layerId !== undefined && patch.layerId !== element.category) parameters.Layer = patch.layerId;
            updates.parameters = parameters;
        }
        if (Object.keys(updates).length === 0) return false;
        bimStore.updateElement(id, updates as Partial<BIMElement>);
        return true;
    }

    // cad
    const app = useAppStore.getState();
    if (!app.entities[id]) return false;
    const updates: Record<string, unknown> = {};
    if (patch.displayName !== undefined) updates.name = patch.displayName;
    if (patch.visible !== undefined) updates.visible = patch.visible;
    if (patch.locked !== undefined) updates.locked = patch.locked;
    if (patch.layerId !== undefined) updates.layer = patch.layerId;
    if (Object.keys(updates).length === 0) return false;
    app.updateEntity(id, updates as Partial<Entity>);
    return true;
}
